import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';

import DropZone from 'components/Shared/DropZone';
import DepotGestionSelect from 'components/Shared/DepotGestionSelect';
import PieceGestionSearch from 'components/Shared/PieceGestionSearch';
import LigneGestionQualification from 'components/Shared/LigneGestionQualification';
import DepannageDirectClient from 'components/Shared/DepannageDirectClient';
import MultiSelectMailContactClient from 'components/Shared/MultiSelectMailContactClient';
import ColisLogistiquePhoto from 'components/Shared/ColisLogistiquePhoto';
import MyDatePicker from 'components/Shared/MyDatePicker';

import { ReclamationQualificationDetailType } from 'constants.js';
import { ReclamationQualificationDetailCodes } from 'constants.js';
import { AnomalieType } from 'constants.js';
import { TypeCodes } from 'constants.js';
import { AdresseDestinations } from 'constants.js';

import stringHelper from 'utils/stringHelper';
import { sansDepotMagasin } from 'utils/depotHelper';

const styles = theme => ({
    flexContainer: {
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    row: {
        flexBasis: '100%',
        marginBottom: '10px'
    },
    column: {
        flexDirection: 'column'
    },
    italic: {
        fontStyle: 'italic'
    },
    formControl: {
        margin: theme.spacing(1)
    },
    group: {
        margin: theme.spacing(1, 0)
    },
    paper: {
        padding: theme.spacing(1),
        flexGrow: 1
    },

    accordionDetailsRow: {
        flexDirection: 'column'
    },



    card: {
        height: '100%',
        border: theme.palette.primary.main + ' 1px solid',
        transition: 'border-color 1s',
        '&:hover': {
            border: theme.palette.secondary[500] + ' 1px solid',
        }
    }
});

const typeQuantiteLabel = "Je n'avais pas commandé cette quantité";
// DEV-4347 : l'ancien choix id 0 « il m'en manque OU j'en ai trop » est scindé en deux (id 3 = manque bien
// facturé, id 4 = trop non facturé), car ils diffèrent sur la quantité retournable :
//  - « il m'en manque » (bien facturé) est soumis au retournable → capperSaisie: true ;
//  - « j'en ai trop » (non facturé) est une déclaration d'écart sans retour financier → capperSaisie: false.
// L'id 0 est conservé (autorise: false → jamais proposé) pour ne pas casser les dossiers déjà créés avec
// cette valeur. L'ordre du tableau = ordre d'affichage ; la valeur stockée est l'id (cf. RadioGroup),
// donc réordonner n'impacte pas les données. capperSaisie : plafonne la saisie à la quantité retournable.
const typeQuantiteLabels = [
    {
        id: 0,
        label: "Il m'en manque (et c'est bien facturé) ou j'en ai trop (et ce n'est pas facturé)",
        autorise: (agePiece, reclamationType, exclusionControleAgePiece) => false,
        capperSaisie: false
    },
    {
        id: 3,
        label: "Il m'en manque (et c'est bien facturé)",
        // DEV-4574 : manque bien facturé → saisie plafonnée à la quantité retournable.
        autorise: (agePiece, reclamationType, exclusionControleAgePiece) => true,
        capperSaisie: true
    },
    {
        id: 4,
        label: "J'en ai trop (et ce n'est pas facturé)",
        // Surplus non facturé : pas d'avoir financier → non soumis à la quantité retournable.
        autorise: (agePiece, reclamationType, exclusionControleAgePiece) => true,
        capperSaisie: false
    },
    {
        id: 1,
        label: "C'est la quantité que j'ai commandé mais j'en ai trop (et c'est bien facturé) => Formulaire prérempli : Demande de retour / le client en a trop demandé",
        autorise: (agePiece, reclamationType, exclusionControleAgePiece) => reclamationType.code !== TypeCodes.Cession,
        capperSaisie: true
    },
    {
        id: 2,
        label: "Je n'ai pas commandé cette quantité (et c'est bien facturé) => Demande de retour / le client n'a pas commandé cette quantité",
        autorise: (agePiece, reclamationType, exclusionControleAgePiece) => (exclusionControleAgePiece || agePiece < 30) && reclamationType.code !== TypeCodes.Cession,
        capperSaisie: true
    }
];
// Attention, traitement particulier sur le choix 1 : chercher ReclamationQualificationDetailCodes.TypeQuantite

// DEV-4347 — helpers dérivés du typeQuantite (valeur = id, en string), source de vérité = typeQuantiteLabels.
// « Sélection de ligne » (ancien id 0, désormais 3/4) : parcours avec sélection de lignes + logique photo/colis.
const typeQuantiteSelectionLigne = ['0', '3', '4'];
const estTypeQuantiteSelectionLigne = (v) => typeQuantiteSelectionLigne.includes(v);
// Plafonnement de la saisie à la quantité retournable, piloté par la config ci-dessus.
const capperSaisieTypeQuantite = (v) => {
    const t = typeQuantiteLabels.find(x => x.id.toString() === v);
    return t ? !!t.capperSaisie : false;
};
// Recherche par id (la valeur stockée). NE PAS indexer typeQuantiteLabels par la valeur : l'ordre du
// tableau (0/3/4/1/2) ne correspond plus aux id depuis la scission DEV-4347.
const getTypeQuantiteLabel = (v) => {
    const t = typeQuantiteLabels.find(x => x.id.toString() === v);
    return t ? t.label : '';
};


const modeRetourLabel = "Choix du mode de retour";
const modeRetour = [
    "Un transporteur doit venir à cette adresse",
    "Le client dépose la marchandise dans un magasin",
    "Le client souhaite prendre en charge lui-même le retour plateforme",
    "Le client remet la marchandise à l'ATC"
];

const modePriseEnChargeLabel = "Veuillez sélectionner le mode de prise en charge. ";
const modePriseEnCharge = [
    "Le client s'occupe lui-même du transport",
    "Foussier envoie une étiquette transporteur pré-payée (Express uniquement)"
];

const modePriseEnChargeATCLabel = "Veuillez sélectionner le mode de prise en charge. ";
const modePriseEnChargeATC = [
    "L'ATC déposera la marchandise au magasin",
    "L'ATC effectuera le retour directement à la plateforme de départ"
];

const typeErreurLabel = "Préciser le type d'erreur ";
const typeErreurLabels = [
    "Erreur de saisie code article",
    "Erreur de préconisation"
];
const fautifLabels = [
    "ATC",
    "ATCS",
    "Magasin",
];




/* Liste des réponses possibles */
const photoConsulteeLabel = "Avez-vous pris connaissance de la photo ? ";
const photoConsulteeLabels = [
    "Oui, j'ai trouvé les informations souhaitées et tout est conforme (dossier après-vente clôturé automatiquement).",
    "Oui, mais je souhaite créer le dossier après-vente"
];


const qualificationDetailLabels =
{
    [ReclamationQualificationDetailCodes.Poids]: "Poids théorique (en g)",
    [ReclamationQualificationDetailCodes.NombreColis]: "Nombre de colis",
    [ReclamationQualificationDetailCodes.NombrePalettes]: "Nombre de palette",
    [ReclamationQualificationDetailCodes.NombreLongueurs]: "Nombre de longueur > 1,20 m",
    [ReclamationQualificationDetailCodes.DateEnlevement]: "Date d'enlèvement souhaitée",
}

const clientInformeLabel = "J'ai informé le client des conditions de reprise.";

const Etape = {
    ChoixDeLaPiece: 0,
    Qualifications: 1
};

class QuantiteArticleIncorrecteStep extends React.Component {

    constructor(props) {
        super(props);

        this.state = {

            expandedPanel: 'panelPiece',
            etape: Etape.ChoixDeLaPiece,

            adresseEnlevementNom: this.props.adresse && this.props.adresse.nomLivraison ? this.props.adresse.nomLivraison : '',
            adresseEnlevementAdresse1: this.props.adresse && this.props.adresse.adresse1 ? this.props.adresse.adresse1 : '',
            adresseEnlevementAdresse2: this.props.adresse && this.props.adresse.adresse2 ? this.props.adresse.adresse2 : '',
            adresseEnlevementCodePostal: this.props.adresse && this.props.adresse.codePostal ? this.props.adresse.codePostal : '',
            adresseEnlevementVille: this.props.adresse && this.props.adresse.ville ? this.props.adresse.ville : '',

            donneurOrdreMail: this.props.donneurOrdre && this.props.donneurOrdre.mail ? this.props.donneurOrdre.mail : '',
            donneurOrdreNom: this.props.donneurOrdre && this.props.donneurOrdre.nom ? this.props.donneurOrdre.nom : '',
            donneurOrdreTelephone: this.props.donneurOrdre && this.props.donneurOrdre.telephoneMobile ? this.props.donneurOrdre.telephoneMobile : this.props.donneurOrdre && this.props.donneurOrdre.telephoneFixe ? this.props.donneurOrdre.telephoneFixe : '',

            [ReclamationQualificationDetailCodes.Poids]: this.props.poidsTheorique,

            [ReclamationQualificationDetailCodes.NombreColis]: 0,
            [ReclamationQualificationDetailCodes.NombrePalettes]: 0,
            [ReclamationQualificationDetailCodes.NombreLongueurs]: 0,

            [ReclamationQualificationDetailCodes.DateEnlevement]: null,

            adresseDestinationCode: '',

            adresseDestinationNom: '',
            adresseDestinationAdresse1: '',
            adresseDestinationAdresse2: '',
            adresseDestinationCodePostal: '',
            adresseDestinationVille: '',

            typeErreur: '0',
            fautif: '0'
        };
    }

    componentDidUpdate(prevProps) {
        // Typical usage (don't forget to compare props):
        if (this.props.reclamation.numeroPiece !== prevProps.reclamation.numeroPiece) {
            this.setState({
                adresseEnlevementNom: this.props.adresse && this.props.adresse.nomLivraison ? this.props.adresse.nomLivraison : '',
                adresseEnlevementAdresse1: this.props.adresse && this.props.adresse.adresse1 ? this.props.adresse.adresse1 : '',
                adresseEnlevementAdresse2: this.props.adresse && this.props.adresse.adresse2 ? this.props.adresse.adresse2 : '',
                adresseEnlevementCodePostal: this.props.adresse && this.props.adresse.codePostal ? this.props.adresse.codePostal : '',
                adresseEnlevementVille: this.props.adresse && this.props.adresse.ville ? this.props.adresse.ville : '',

                donneurOrdreMail: this.props.donneurOrdre && this.props.donneurOrdre.mail ? this.props.donneurOrdre.mail : '',
                donneurOrdreNom: this.props.donneurOrdre && this.props.donneurOrdre.nom ? this.props.donneurOrdre.nom : '',
                donneurOrdreTelephone: this.props.donneurOrdre && this.props.donneurOrdre.telephoneMobile ? this.props.donneurOrdre.telephoneMobile : this.props.donneurOrdre && this.props.donneurOrdre.telephoneFixe ? this.props.donneurOrdre.telephoneFixe : '',

                [ReclamationQualificationDetailCodes.Poids]: this.props.poidsTheorique,

                adresseDestinationCode: '',


                [ReclamationQualificationDetailCodes.NombreColis]: 0,
                [ReclamationQualificationDetailCodes.NombrePalettes]: 0,
                [ReclamationQualificationDetailCodes.NombreLongueurs]: 0,

                [ReclamationQualificationDetailCodes.DateEnlevement]: null,

                typeErreur: '0',
                fautif: '0'
            });
        }

        if (this.props.adresse !== null && prevProps.adresse === null) {
            this.setState({
                adresseEnlevementNom: this.props.adresse && this.props.adresse.nomLivraison ? this.props.adresse.nomLivraison : '',
                adresseEnlevementAdresse1: this.props.adresse && this.props.adresse.adresse1 ? this.props.adresse.adresse1 : '',
                adresseEnlevementAdresse2: this.props.adresse && this.props.adresse.adresse2 ? this.props.adresse.adresse2 : '',
                adresseEnlevementCodePostal: this.props.adresse && this.props.adresse.codePostal ? this.props.adresse.codePostal : '',
                adresseEnlevementVille: this.props.adresse && this.props.adresse.ville ? this.props.adresse.ville : ''
            });
        }

        if (this.props.poidsTheorique !== prevProps.poidsTheorique) {
            this.setState({
                [ReclamationQualificationDetailCodes.Poids]: this.props.poidsTheorique,
            });
        }
    }

    componentDidMount() {
        // On détermine si on doit rendre accessible le bouton pour passer à l'étape suivante ou le bouton de création.
        const { onNextStepValid } = this.props;
        onNextStepValid(false);
    }

    adresseEnlevementToStr = ({ adresseEnlevementNom, adresseEnlevementAdresse1, adresseEnlevementAdresse2, adresseEnlevementCodePostal, adresseEnlevementVille }) => {
        let adresseStr = adresseEnlevementNom;
        if (adresseEnlevementAdresse1 && adresseEnlevementAdresse1 !== '') {
            adresseStr += '\r\n' + adresseEnlevementAdresse1;
        }
        if (adresseEnlevementAdresse2 && adresseEnlevementAdresse2 !== '') {
            adresseStr += '\r\n' + adresseEnlevementAdresse2;
        }
        adresseStr += '\r\n' + adresseEnlevementCodePostal + ' ' + adresseEnlevementVille;

        return adresseStr;
    }

    adresseDestinationToStr = ({ adresseDestinationCode, adresseDestinationNom, adresseDestinationAdresse1, adresseDestinationAdresse2, adresseDestinationCodePostal, adresseDestinationVille }) => {
        if (adresseDestinationCode === 3) {

            let adresseStr = adresseDestinationNom;
            if (adresseDestinationAdresse1 && adresseDestinationAdresse1 !== '') {
                adresseStr += '\r\n' + adresseDestinationAdresse1;
            }
            if (adresseDestinationAdresse2 && adresseDestinationAdresse2 !== '') {
                adresseStr += '\r\n' + adresseDestinationAdresse2;
            }
            adresseStr += '\r\n' + adresseDestinationCodePostal + ' ' + adresseDestinationVille;

            return adresseStr;
        }

        let adresse = AdresseDestinations.find(x => x.id === adresseDestinationCode);

        if (adresse) {
            return adresse.nom;
        }

        return '';
    }

    donneurOrdreToStr = ({ donneurOrdreNom, donneurOrdreMail, donneurOrdreTelephone }) => {
        return donneurOrdreNom + ' / ' + donneurOrdreMail + ' / ' + donneurOrdreTelephone;
    }


    // Obtention d'un détail quelconque.
    getDetail(code, typeValeur) {
        const { reclamation } = this.props;

        // Initialisation de la valeur
        let valeur = '';

        // Obtention du code
        const detail = reclamation.qualificationDetails.find(detail => detail.code === code);

        // Affectation si possible de la valeur en fonction du type demandé
        if (detail !== undefined) {
            valeur = detail[typeValeur];
        }

        return valeur;
    }

    getDepot() {
        const { reclamation } = this.props;

        let depot = '';

        const detailDepot = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Depot);
        if (detailDepot !== undefined) {
            depot = detailDepot.valeurString;
        }

        return depot;
    }

    getMagasinRetourATC() {
        const { reclamation } = this.props;

        let magasinRetourATC = '';

        const detailMagasinRetourATC = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.MagasinRetourATC);
        if (detailMagasinRetourATC !== undefined) {
            magasinRetourATC = detailMagasinRetourATC.valeurString;
        }

        return magasinRetourATC;
    }

    getDepannageDirectClient() {
        const { reclamation } = this.props;

        let depannageDirectClient = '';

        const detailDepannageDirectClient = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.DepannageDirectClient);
        if (detailDepannageDirectClient !== undefined) {
            depannageDirectClient = detailDepannageDirectClient.valeurBool;
        }

        return depannageDirectClient;
    }

    getTypeQuantite() {
        const { reclamation } = this.props;

        let typeQuantite = '';

        const detailTypeQuantite = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.TypeQuantite);
        if (detailTypeQuantite !== undefined) {
            typeQuantite = detailTypeQuantite.valeurInteger.toString();
        }

        return typeQuantite;
    }

    getModeRetour() {
        const { reclamation } = this.props;

        let modeRetour = '';

        const detailModeRetour = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ModeRetour);
        if (detailModeRetour !== undefined) {
            modeRetour = detailModeRetour.valeurInteger.toString();
        }

        return modeRetour;
    }

    getModePriseEnCharge() {
        const { reclamation } = this.props;

        let modePriseEnCharge = '';

        const detailModePriseEnCharge = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ModePriseEnCharge);
        if (detailModePriseEnCharge !== undefined) {
            modePriseEnCharge = detailModePriseEnCharge.valeurInteger.toString();
        }

        return modePriseEnCharge;
    }

    getModePriseEnChargeATC() {
        const { reclamation } = this.props;

        let modePriseEnChargeATC = '';

        const detailModePriseEnChargeATC = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
        if (detailModePriseEnChargeATC !== undefined) {
            modePriseEnChargeATC = detailModePriseEnChargeATC.valeurInteger.toString();
        }

        return modePriseEnChargeATC;
    }

    getUtilisateurMagasin() {
        let utilisateurCourant = JSON.parse(localStorage.getItem('utilisateur'));

        if (utilisateurCourant && utilisateurCourant.groupes.find(x => x.code === 'MAGASIN') !== undefined) {
            return true;
        } else {
            return false;
        }
    }

    getClientInforme() {
        const { reclamation } = this.props;

        let clientInforme = false;

        const detailClientInforme = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ClientInforme);
        if (detailClientInforme !== undefined) {
            clientInforme = detailClientInforme.valeurBool;
        }

        return clientInforme;
    }

    getEmails() {
        const { reclamation } = this.props;

        let emails = false;

        const detailEmails = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Emails);
        if (detailEmails !== undefined) {
            emails = detailEmails.valeurString;
        }

        return emails;
    }

    getPlateformeRetour() {
        const { reclamation } = this.props;

        let plateformeRetour = '';

        const detail = reclamation.qualificationDetails.find(d => d.code === ReclamationQualificationDetailCodes.PlateformeRetour);
        if (detail !== undefined) {
            plateformeRetour = detail.valeurString;
        }

        return plateformeRetour;
    }


    getPhotoConsultee() {
        return this.getDetail(ReclamationQualificationDetailCodes.PhotoConsultee, 'valeurInteger');
    }


    checkErrors(
        newState,
        depannageDirectClient,
        typeQuantite,
        depot,
        modeRetour,
        modePriseEnCharge,
        modePriseEnChargeATC,
        magasinRetourATC,
        clientInforme,
        emails,
        plateformeRetour,
        photoConsultee,
        lignes
    ) {
        const { reclamation, utilisateurMagasin, colisLogistiques } = this.props;

        let currentState = newState;

        if (!currentState) {
            currentState = { ...this.state }
        }

        let currentLignes = lignes;
        if (!currentLignes) {
            currentLignes = [...reclamation.lignes];
        }

        let errors = [];

        if (typeQuantite === undefined || typeQuantite === '') {
            errors.push({ key: ReclamationQualificationDetailCodes.TypeQuantite, errorMsg: 'Veuillez sélectionner un choix dans la liste des erreurs de quantité.' });
            errors.push({ key: ReclamationQualificationDetailCodes.NbLignes, errorMsg: null });
        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.TypeQuantite, errorMsg: null });


            if (typeQuantite === '1') {
                errors.push({ key: ReclamationQualificationDetailCodes.NbLignes, errorMsg: null });
            } else {
                if (currentLignes.length === 0) {
                    errors.push({ key: ReclamationQualificationDetailCodes.NbLignes, errorMsg: 'Au moins une ligne doit être renseignée.' });
                } else {
                    errors.push({ key: ReclamationQualificationDetailCodes.NbLignes, errorMsg: null });
                }
            }
        }

        if (reclamation.type.code === TypeCodes.Client && utilisateurMagasin) {
            if (depannageDirectClient === '') {
                errors.push({ key: ReclamationQualificationDetailCodes.DepannageDirectClient, errorMsg: 'Avez-vous résolu le problème de votre client ?' });
            } else {
                errors.push({ key: ReclamationQualificationDetailCodes.DepannageDirectClient, errorMsg: null });
            }
        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.DepannageDirectClient, errorMsg: null });
        }

        if (reclamation.numeroPiece.length === 0) {
            errors.push({ key: ReclamationQualificationDetailCodes.Piece, errorMsg: 'Veuillez sélectionner une pièce.' });
        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.Piece, errorMsg: null });
        }

        if (estTypeQuantiteSelectionLigne(typeQuantite)) {

            if (colisLogistiques.length > 0) { // TODO revoir les cas d'erreurs

                let reclaCodes = currentLignes.map(x => x.codeArticle);

                let colisLogistiquesPhoto = colisLogistiques.map(x => {

                    let codesArticles = x.lignes.map(x => x.codeArticle);
                    let c = {
                        photoId: x.photoId,
                        numeroSupport: x.numeroSupport,
                        concerneRecla: reclaCodes.some(r => codesArticles.indexOf(r) >= 0)
                    };
                    return c;
                });

                let afficherQuestion = colisLogistiquesPhoto.some(x => x.concerneRecla && x.photoId);

                //console.log("reclaCodes", reclaCodes);
                //console.log("colisLogistiquesPhoto", colisLogistiquesPhoto);
                //console.log("afficherQuestion", afficherQuestion);
                //console.log("photoConsultee", photoConsultee);

                if (afficherQuestion) {
                    if (photoConsultee === '') {
                        errors.push({ key: ReclamationQualificationDetailCodes.PhotoConsultee, errorMsg: photoConsulteeLabel });
                    } else {
                        errors.push({ key: ReclamationQualificationDetailCodes.PhotoConsultee, errorMsg: null });
                    }
                } else {
                    errors.push({ key: ReclamationQualificationDetailCodes.PhotoConsultee, errorMsg: null });
                }
            }

        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.PhotoConsultee, errorMsg: null });
        }


        if (typeQuantite === '2') {
            if (modeRetour === '') {
                errors.push({ key: ReclamationQualificationDetailCodes.ModeRetour, errorMsg: 'Le mode de retour doit être renseigné.' });
            } else {
                errors.push({ key: ReclamationQualificationDetailCodes.ModeRetour, errorMsg: null });
            }



            if (clientInforme !== true) {
                errors.push({ key: ReclamationQualificationDetailCodes.ClientInforme, errorMsg: 'Veuillez confirmer que le client a été informé des conditions de retour de la marchandise..' });
            } else {
                errors.push({ key: ReclamationQualificationDetailCodes.ClientInforme, errorMsg: null });
            }



            if (!emails) {
                errors.push({ key: ReclamationQualificationDetailCodes.Emails, errorMsg: 'Veuillez spécifier un mail pour la notification.' });
            }
            else {
                errors.push({ key: ReclamationQualificationDetailCodes.Emails, errorMsg: null });
            }

        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.ModeRetour, errorMsg: null });
        }

        if (typeQuantite === '2' && modeRetour === '1') {
            if (depot === '') {
                errors.push({ key: ReclamationQualificationDetailCodes.Depot, errorMsg: 'Veuillez choisir un magasin' });
            }
            else {
                errors.push({ key: ReclamationQualificationDetailCodes.Depot, errorMsg: null });
            }
        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.Depot, errorMsg: null });
        }

        if (typeQuantite === '2' && modeRetour === '2') {
            if (modePriseEnCharge === '') {
                errors.push({ key: ReclamationQualificationDetailCodes.ModePriseEnCharge, errorMsg: 'Le mode de prise en charge doit être renseigné.' });
            } else {
                errors.push({ key: ReclamationQualificationDetailCodes.ModePriseEnCharge, errorMsg: null });


                if (modePriseEnCharge === 0) {

                    if (plateformeRetour === '') {
                        errors.push({ key: ReclamationQualificationDetailCodes.PlateformeRetour, errorMsg: 'Veuillez sélectionner une plateforme' });
                    }
                    else {
                        errors.push({ key: ReclamationQualificationDetailCodes.PlateformeRetour, errorMsg: null });
                    }
                }


            }
        } else {
            errors.push({ key: ReclamationQualificationDetailCodes.ModePriseEnCharge, errorMsg: null });
        }

        if (typeQuantite === '2' && (modeRetour === '0' || (modeRetour === '2' && modePriseEnCharge === '1'))) {

            // Les données d'adresse sont nécessaires
            if (stringHelper.isNullOrWhitespace(currentState.adresseEnlevementNom)
                || stringHelper.isNullOrWhitespace(currentState.adresseEnlevementCodePostal)
                || stringHelper.isNullOrWhitespace(currentState.adresseEnlevementVille)
                || (stringHelper.isNullOrWhitespace(currentState.adresseEnlevementAdresse1) && stringHelper.isNullOrWhitespace(currentState.adresseEnlevementAdresse2))
            ) {
                errors.push({ key: ReclamationQualificationDetailCodes.AdresseEnlevement, errorMsg: 'Il manque des informations sur l\'adresse d\'enlèvement.' });
            }
            else {
                errors.push({ key: ReclamationQualificationDetailCodes.AdresseEnlevement, errorMsg: null });
            }

            if (currentState.adresseDestinationCode === 3) {

                if (stringHelper.isNullOrWhitespace(currentState.adresseDestinationNom)
                    || stringHelper.isNullOrWhitespace(currentState.adresseDestinationCodePostal)
                    || stringHelper.isNullOrWhitespace(currentState.adresseDestinationVille)
                    || (stringHelper.isNullOrWhitespace(currentState.adresseDestinationAdresse1) && stringHelper.isNullOrWhitespace(currentState.adresseDestinationAdresse2))
                ) {
                    errors.push({ key: ReclamationQualificationDetailCodes.AdresseDestination, errorMsg: 'Il manque des informations sur l\'adresse de destination.' });
                }
                else {
                    errors.push({ key: ReclamationQualificationDetailCodes.AdresseDestination, errorMsg: null });
                }

            }
            else {
                if (currentState.adresseDestinationCode === '') {
                    errors.push({ key: ReclamationQualificationDetailCodes.AdresseDestination, errorMsg: 'Adresse de destination manquante.' });
                } else {
                    errors.push({ key: ReclamationQualificationDetailCodes.AdresseDestination, errorMsg: null });
                }
            }

            if (stringHelper.isNullOrWhitespace(currentState.donneurOrdreNom)
                && stringHelper.isNullOrWhitespace(currentState.donneurOrdreMail)
                && stringHelper.isNullOrWhitespace(currentState.donneurOrdreTelephone)
            ) {
                errors.push({ key: ReclamationQualificationDetailCodes.ContactClient, errorMsg: 'Il manque des informations sur le contact client.' });
            }
            else {
                errors.push({ key: ReclamationQualificationDetailCodes.ContactClient, errorMsg: null });
            }

            if (!currentState[ReclamationQualificationDetailCodes.Poids]) {
                errors.push({ key: ReclamationQualificationDetailCodes.Poids, errorMsg: 'Veuillez spécifier le poids théorique du retour.' });
            }
            else {
                errors.push({ key: ReclamationQualificationDetailCodes.Poids, errorMsg: null });
            }

            if (currentState[ReclamationQualificationDetailCodes.NombrePalettes] === 0
                && currentState[ReclamationQualificationDetailCodes.NombreColis] === 0
                && currentState[ReclamationQualificationDetailCodes.NombreLongueurs] === 0) {
                errors.push({ key: ReclamationQualificationDetailCodes.NombreColis, errorMsg: 'Veuillez spécifier le colisage.' });
            }
            else {
                errors.push({ key: ReclamationQualificationDetailCodes.NombreColis, errorMsg: null });
            }
        }
        else {
            errors.push({ key: ReclamationQualificationDetailCodes.AdresseEnlevement, errorMsg: null });
            errors.push({ key: ReclamationQualificationDetailCodes.AdresseDestination, errorMsg: null });
            errors.push({ key: ReclamationQualificationDetailCodes.ContactClient, errorMsg: null });
            errors.push({ key: ReclamationQualificationDetailCodes.Poids, errorMsg: null });
            errors.push({ key: ReclamationQualificationDetailCodes.NombreColis, errorMsg: null });
        }

        if (typeQuantite === '2' && modeRetour === '3') {
            if (modePriseEnChargeATC === '') {
                errors.push({ key: ReclamationQualificationDetailCodes.ModePriseEnChargeATC, errorMsg: 'Le mode de prise en charge doit être renseigné.' });
            } else {
                errors.push({ key: ReclamationQualificationDetailCodes.ModePriseEnChargeATC, errorMsg: null });

                if (modePriseEnChargeATC === '0') {
                    // Le magasin est obligatoire
                    if (magasinRetourATC === '') {
                        errors.push({ key: ReclamationQualificationDetailCodes.MagasinRetourATC, errorMsg: 'Veuillez spécifier le magasin de retour.' });
                    } else {
                        errors.push({ key: ReclamationQualificationDetailCodes.MagasinRetourATC, errorMsg: null });
                    }
                } else {
                    errors.push({ key: ReclamationQualificationDetailCodes.MagasinRetourATC, errorMsg: null });
                }
            }
        }

        return errors;
    }

    handleChangePanel = panel => (event, expandedPanel) => {
        this.setState({
            expandedPanel: expandedPanel ? panel : false
        });
    }

    handleChange = (event) => {
        const { onChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);

        const errors = this.checkErrors(newState, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onChange(event, errors);
    }

    handleChangeTypeErreur = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);


        const qualificationDetail = {
            position: 1,
            code: ReclamationQualificationDetailCodes.TypeErreurCommande,
            type: ReclamationQualificationDetailType.Texte,
            libelle: typeErreurLabel,
            valeurString: typeErreurLabels[newState.typeErreur] + ' ' + fautifLabels[newState.fautif]
        };

        const errors = this.checkErrors(newState, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeInteger = (event) => {
        const { onDetailChange } = this.props;

        let newState;

        newState = {
            ...this.state,
            [event.target.name]: Math.max(event.target.value, 0)
        };

        this.setState(newState);

        const qualificationDetail = {
            position: parseInt(event.target.attributes['position'].nodeValue, 10),
            code: event.target.name,
            type: ReclamationQualificationDetailType.Nombre,
            libelle: qualificationDetailLabels[event.target.name],
            valeurInteger: parseInt(event.target.value, 10)

        };

        const errors = this.checkErrors(newState, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeDate = (event) => {
        const { onDetailChange } = this.props;

        let newState;

        newState = {
            ...this.state,
            [event.target.name]: event.target.value
        };

        this.setState(newState);

        const qualificationDetails = [];
        const detailsToDelete = [];

        if (event.target.value) {
            qualificationDetails.push({
                position: 7,
                code: event.target.name,
                type: ReclamationQualificationDetailType.Date,
                libelle: qualificationDetailLabels[event.target.name],
                valeurDate: event.target.value.toDateString()

            });
        }
        else {
            detailsToDelete.push(event.target.name);
        }

        const errors = this.checkErrors(
            newState,
            this.getDepannageDirectClient(),
            this.getTypeQuantite(),
            this.getDepot(),
            this.getModeRetour(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getClientInforme(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getPhotoConsultee(),
            null);

        onDetailChange(qualificationDetails, errors, detailsToDelete);
    }

    handleSelectPiece = (value) => {
        const { onSelectPiece } = this.props;

        // On ouvre le panel avec les lignes de la pièce.
        this.setState({
            expandedPanel: 'panelQuantite',
            etape: Etape.Qualifications
        });

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onSelectPiece(value, errors);
    }

    handleQualificationDetailsDepannageDirectClientChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 20, // A garder au delà du nombre des autres détails
            code: ReclamationQualificationDetailCodes.DepannageDirectClient,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: 'Avez-vous résolu le problème de votre client ?',
            valeurBool: event.target.value
        };

        const errors = this.checkErrors(null, event.target.value, this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsTypeQuantiteChange = (event) => {
        const { onDetailChange } = this.props;
        const { typeErreur, fautif } = this.state;

        const qualificationDetails = [{
            position: 0,
            code: ReclamationQualificationDetailCodes.TypeQuantite,
            type: ReclamationQualificationDetailType.ChoixMultiple,
            libelle: typeQuantiteLabel,
            valeurString: getTypeQuantiteLabel(event.target.value),
            valeurInteger: parseInt(event.target.value, 10)
        }];

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), event.target.value, this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        let detailsToDelete = [ReclamationQualificationDetailCodes.PhotoConsultee];


        if (event.target.value === '1') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.NbLignes);
        }

        if (event.target.value === '2') {
            qualificationDetails.push(
                {
                    position: 1,
                    code: ReclamationQualificationDetailCodes.TypeErreurCommande,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: typeErreurLabel,
                    valeurString: typeErreurLabels[typeErreur] + ' ' + fautifLabels[fautif]
                }
            );
        }

        // Cas sans mode de retour (sélection de ligne 0/3/4, ou prérempli id 1) : on purge les détails
        // liés au mode de retour / enlèvement.
        if (estTypeQuantiteSelectionLigne(event.target.value) || event.target.value === '1') {

            detailsToDelete.push(ReclamationQualificationDetailCodes.ModeRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);

            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);

            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseDestination);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ContactClient);
            detailsToDelete.push(ReclamationQualificationDetailCodes.DateEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.Poids);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreColis);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombrePalettes);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreLongueurs);
            detailsToDelete.push(ReclamationQualificationDetailCodes.TransporteurInitial);

            detailsToDelete.push(ReclamationQualificationDetailCodes.TypeErreurCommande);
        }

        onDetailChange(qualificationDetails, errors, detailsToDelete);

        if (estTypeQuantiteSelectionLigne(event.target.value) || event.target.value === '2') {
            this.setState({
                ...this.state,
                expandedPanel: 'panelLignes'
            });
        }
    }

    handleQualificationDetailsModeRetourChange = (event) => {
        const { onDetailChange, reclamation } = this.props;

        let codeDepot = this.getDepot();

        const qualificationDetails = [{
            position: 2,
            code: ReclamationQualificationDetailCodes.ModeRetour,
            type: ReclamationQualificationDetailType.ChoixMultiple,
            libelle: modeRetourLabel,
            valeurString: modeRetour[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        }];

        let detailsToDelete = [];

        detailsToDelete.push(ReclamationQualificationDetailCodes.PlateformeRetour);
        detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseRetour);

        // "Un transporteur doit venir à cette adresse",
        if (event.target.value === '0') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);

            qualificationDetails.push(
                {
                    position: 4,
                    code: ReclamationQualificationDetailCodes.AdresseEnlevement,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse d'enlèvement",
                    valeurString: this.adresseEnlevementToStr(this.state)
                });
            qualificationDetails.push(
                {
                    position: 5,
                    code: ReclamationQualificationDetailCodes.AdresseDestination,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse de destination",
                    valeurString: this.adresseDestinationToStr(this.state),
                    valeurInteger: this.state.adresseDestinationCode
                });

            qualificationDetails.push(
                {
                    position: 6,
                    code: ReclamationQualificationDetailCodes.ContactClient,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact",
                    valeurString: this.donneurOrdreToStr(this.state)
                });

            if (this.state[ReclamationQualificationDetailCodes.DateEnlevement] !== null) {
                qualificationDetails.push(
                    {
                        position: 7,
                        code: ReclamationQualificationDetailCodes.DateEnlevement,
                        type: ReclamationQualificationDetailType.Date,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.DateEnlevement],
                        valeurDate: this.state[ReclamationQualificationDetailCodes.DateEnlevement].toDateString()
                    });
            }

            qualificationDetails.push(
                {
                    position: 8,
                    code: ReclamationQualificationDetailCodes.Poids,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.Poids],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.Poids]
                });

            qualificationDetails.push(
                {
                    position: 9,
                    code: ReclamationQualificationDetailCodes.NombrePalettes,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombrePalettes],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombrePalettes]
                });

            qualificationDetails.push(
                {
                    position: 10,
                    code: ReclamationQualificationDetailCodes.NombreColis,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreColis],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreColis]
                });

            qualificationDetails.push(
                {
                    position: 11,
                    code: ReclamationQualificationDetailCodes.NombreLongueurs,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreLongueurs],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreLongueurs]
                });


            qualificationDetails.push(
                {
                    position: 12,
                    code: ReclamationQualificationDetailCodes.TransporteurInitial,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Transporteur initial",
                    valeurString: this.props.transporteurInitial ? this.props.transporteurInitial.nom + ' / ' + this.props.transporteurInitial.contrat : ''
                });
        }
        // "Le client dépose la marchandise dans un magasin",
        if (event.target.value === '1') {

            codeDepot = reclamation.magasin ? reclamation.magasin.codeDepot : '';

            if (codeDepot === '') {
                let utilisateur = JSON.parse(localStorage.getItem('utilisateur'));

                if (sansDepotMagasin(utilisateur)) {
                    if (utilisateur.depots && utilisateur.depots.length === 1) {
                        codeDepot = utilisateur.depots[0].code;
                    }
                }
                else {
                    codeDepot = utilisateur.depot.code;
                }
            }

            if (codeDepot !== '') {
                qualificationDetails.push(
                    {
                        position: 3,
                        code: ReclamationQualificationDetailCodes.Depot,
                        type: ReclamationQualificationDetailType.CodeDepot,
                        libelle: "Magasin",
                        valeurString: codeDepot
                    });
            }



            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
        }
        // "Le client souhaite prendre en charge lui-même le retour plateforme"
        if (event.target.value === '2') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
        }

        if (event.target.value === '1' || event.target.value === '2' || event.target.value === '3') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseDestination);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ContactClient);
            detailsToDelete.push(ReclamationQualificationDetailCodes.DateEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.Poids);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreColis);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombrePalettes);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreLongueurs);
            detailsToDelete.push(ReclamationQualificationDetailCodes.TransporteurInitial);
        }

        if (event.target.value === '3') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
        }


        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), codeDepot, event.target.value, this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);


        onDetailChange(qualificationDetails, errors, detailsToDelete);

        if (event.target.value === '2') {
            this.setState({
                ...this.state,
                expandedPanel: 'panelModePriseEnCharge'
            });
        }

        if (event.target.value === '3') {
            this.setState({
                ...this.state,
                expandedPanel: 'panelModePriseEnChargeATC'
            });
        }
    }

    handleQualificationDetailsModePriseEnChargeChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetails = [{
            position: 3,
            code: ReclamationQualificationDetailCodes.ModePriseEnCharge,
            type: ReclamationQualificationDetailType.ChoixMultiple,
            libelle: modePriseEnChargeLabel,
            valeurString: modePriseEnCharge[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        }];

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), event.target.value, this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        let detailsToDelete = [];
        detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
        detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);

        // "Le client s'occupe lui-même du transport",
        if (event.target.value === '0') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseDestination);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ContactClient);
            detailsToDelete.push(ReclamationQualificationDetailCodes.DateEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreColis);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombrePalettes);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreLongueurs);
            detailsToDelete.push(ReclamationQualificationDetailCodes.TransporteurInitial);
        }
        // "Foussier envoie une étiquette transporteur pré-payée (Express uniquement)"
        if (event.target.value === '1') {
            qualificationDetails.push(
                {
                    position: 4,
                    code: ReclamationQualificationDetailCodes.AdresseEnlevement,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse d'enlèvement",
                    valeurString: this.adresseEnlevementToStr(this.state)
                });
            qualificationDetails.push(
                {
                    position: 5,
                    code: ReclamationQualificationDetailCodes.AdresseDestination,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse de destination",
                    valeurString: this.adresseDestinationToStr(this.state),
                    valeurInteger: this.state.adresseDestinationCode
                });

            qualificationDetails.push(
                {
                    position: 6,
                    code: ReclamationQualificationDetailCodes.ContactClient,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact",
                    valeurString: this.donneurOrdreToStr(this.state)
                });

            if (this.state[ReclamationQualificationDetailCodes.DateEnlevement] !== null) {
                qualificationDetails.push(
                    {
                        position: 7,
                        code: ReclamationQualificationDetailCodes.DateEnlevement,
                        type: ReclamationQualificationDetailType.Date,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.DateEnlevement],
                        valeurDate: this.state[ReclamationQualificationDetailCodes.DateEnlevement].toDateString()
                    });

            }

            qualificationDetails.push(
                {
                    position: 8,
                    code: ReclamationQualificationDetailCodes.Poids,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.Poids],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.Poids]
                });

            qualificationDetails.push(
                {
                    position: 9,
                    code: ReclamationQualificationDetailCodes.NombrePalettes,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombrePalettes],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombrePalettes]
                });

            qualificationDetails.push(
                {
                    position: 10,
                    code: ReclamationQualificationDetailCodes.NombreColis,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreColis],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreColis]
                });

            qualificationDetails.push(
                {
                    position: 11,
                    code: ReclamationQualificationDetailCodes.NombreLongueurs,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreLongueurs],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreLongueurs]
                });

            qualificationDetails.push(
                {
                    position: 12,
                    code: ReclamationQualificationDetailCodes.TransporteurInitial,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Transporteur initial",
                    valeurString: this.props.transporteurInitial ? this.props.transporteurInitial.nom + ' / ' + this.props.transporteurInitial.contrat : ''
                });

        }

        onDetailChange(qualificationDetails, errors, detailsToDelete);
    }

    handleQualificationDetailsModePriseEnChargeATCChange = (event) => {
        const { onDetailChange, reclamation } = this.props;

        let detailsToDelete = [];

        let magasinRetourATC = this.getMagasinRetourATC();

        const qualificationDetails = [{
            position: 3,
            code: ReclamationQualificationDetailCodes.ModePriseEnChargeATC,
            type: ReclamationQualificationDetailType.ChoixMultiple,
            libelle: modePriseEnChargeATCLabel,
            valeurString: modePriseEnChargeATC[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        }];


        // L'ATC déposera la marchandise au magasin
        if (event.target.value === '0') {

            magasinRetourATC = reclamation.magasin ? reclamation.magasin.codeDepot : '';

            qualificationDetails.push(
                {
                    position: 5,
                    code: ReclamationQualificationDetailCodes.MagasinRetourATC,
                    type: ReclamationQualificationDetailType.CodeDepot,
                    libelle: "Magasin",
                    valeurString: magasinRetourATC
                });
        }

        // L'ATC retourne la marchandise directement au Monne.
        if (event.target.value === '1') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
        }


        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), event.target.value, magasinRetourATC, this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);



        onDetailChange(qualificationDetails, errors, detailsToDelete);
    }

    handleQualificationDetailsPlateformeRetour = (event, plateformeRetourCode, plateformeRetourObjet) => {
        const { onDetailChange } = this.props;

        let adresse = '';
        if (plateformeRetourObjet !== '') {
            adresse = 'FOUSSIER\n' + plateformeRetourObjet.adresse + '\n' + plateformeRetourObjet.codePostal + ' ' + plateformeRetourObjet.ville;
        }

        const qualificationDetails = [
            {
                position: 5,
                code: ReclamationQualificationDetailCodes.PlateformeRetour,
                type: ReclamationQualificationDetailType.CodeDepot,
                libelle: "Plateforme",
                valeurString: plateformeRetourCode
            },
            {
                position: 6,
                code: ReclamationQualificationDetailCodes.AdresseRetour,
                type: ReclamationQualificationDetailType.Texte,
                libelle: "Adresse retour",
                valeurString: adresse
            }
        ];

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), plateformeRetourCode, this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetails, errors, null);
    }

    handleAddLigne = (ligne) => {
        const { reclamation, onAddLigne, onAddRetour, onRemoveRetour } = this.props;


        let lignes = reclamation.lignes.filter(x => x.numeroLigne !== ligne.numeroLigne);
        lignes.push(ligne);

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), lignes);

        onAddLigne(ligne, errors);


        let typeQuantite = this.getTypeQuantite();
        if (typeQuantite === '2') {

            const montant = Number(lignes.reduce((currentValue, nextValue) => currentValue + (nextValue.prixUnitaire * nextValue.quantiteAnomalie), 0).toFixed(2));

            // On met à jour le noeud retour sur la reclamation.
            //onAddRetour(montant, -1, 0, 0, '', false, 0);

            onAddRetour({
                montant,
                decote: -1,
                decoteJours: 0,
                fdp: 0,
                messageFDP: '',
                autoriseJoker: false,
                pctAvoir12Mois: 0,
                //exclusionDecote: result.exclusionDecote
            });

        }
        else {
            onRemoveRetour();
        }

    }

    handleRemoveLigne = (numeroLigne) => {
        const { reclamation, onRemoveLigne, onAddRetour, onRemoveRetour } = this.props;

        let lignes = reclamation.lignes.filter(x => x.numeroLigne !== numeroLigne);

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), lignes);

        onRemoveLigne(numeroLigne, errors);


        let produitCommande = this.getProduitCommande();
        if (lignes.length > 0 && (produitCommande === '1' || produitCommande === '2')) {

            const montant = Number(lignes.reduce((currentValue, nextValue) => currentValue + (nextValue.prixUnitaire * nextValue.quantiteAnomalie), 0).toFixed(2));

            // On met à jour le noeud retour sur la reclamation.
            // onAddRetour(montant, -1, 0, 0, '', false, 0);

            onAddRetour({
                montant,
                decote: -1,
                decoteJours: 0,
                fdp: 0,
                messageFDP: '',
                autoriseJoker: false,
                pctAvoir12Mois: 0,
                //exclusionDecote: result.exclusionDecote
            });
        }
        else {
            onRemoveRetour();
        }

    }

    handleSelectAllLignes = () => {
        const { onSelectAllLignes } = this.props;

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onSelectAllLignes({ errors, typeAnomalie: AnomalieType.DemandeRetour, commentaire: "" }); // Vérifier s'il faut aussi prendre en compte l'exclusion

    }

    handleChangeAdresseEnlevement = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);

        const qualificationDetail = {
            position: 4,
            code: ReclamationQualificationDetailCodes.AdresseEnlevement,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Adresse d'enlèvement",
            valeurString: this.adresseEnlevementToStr(newState)
        };

        const errors = this.checkErrors(newState, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeAdresseDestination = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        if (event.target.name === "adresseDestinationCode") {
            newState = { ...this.state, [event.target.name]: parseInt(event.target.value, 10) };
        }

        this.setState(newState);


        const qualificationDetail = {
            position: 5,
            code: ReclamationQualificationDetailCodes.AdresseDestination,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Adresse de destination",
            valeurString: this.adresseDestinationToStr(newState),
            valeurInteger: newState.adresseDestinationCode

        };

        const errors = this.checkErrors(newState, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeDonneurOrdre = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);

        const qualificationDetail = {
            position: 6,
            code: ReclamationQualificationDetailCodes.ContactClient,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Contact",
            valeurString: this.donneurOrdreToStr(newState)

        };

        const errors = this.checkErrors(newState, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsCodeDepot = async (event, codeDepot) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.Depot,
            type: ReclamationQualificationDetailType.CodeDepot,
            libelle: "Magasin",
            valeurString: codeDepot
        };

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), codeDepot, this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsMagasinRetourATC = async (event, codeDepot) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 5,
            code: ReclamationQualificationDetailCodes.MagasinRetourATC,
            type: ReclamationQualificationDetailType.CodeDepot,
            libelle: "Magasin",
            valeurString: codeDepot
        };

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), codeDepot, this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }





    handleQualificationDetailsClientInformeChange = ({ checked, position, libelle }) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: position,
            code: ReclamationQualificationDetailCodes.ClientInforme,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: libelle,
            valeurBool: checked
        };

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), checked, this.getEmails(), this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsEmails = (emails) => {
        const { onDetailChange } = this.props;

        let qualificationDetail = null;

        if (emails) {
            qualificationDetail = {
                position: 25,
                code: ReclamationQualificationDetailCodes.Emails,
                type: ReclamationQualificationDetailType.Texte,
                libelle: 'Email(s) client pour notification de la reprise',
                valeurString: emails
            };
        }

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), emails, this.getPlateformeRetour(), this.getPhotoConsultee(), null);

        onDetailChange(qualificationDetail, errors, null);
    }



    handleQualificationDetailsPhotoConsulteeChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 7, // TODO vérifier
            code: ReclamationQualificationDetailCodes.PhotoConsultee,
            type: ReclamationQualificationDetailType.ChoixSimple,
            libelle: photoConsulteeLabel,
            valeurString: photoConsulteeLabels[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        };

        const errors = this.checkErrors(null, this.getDepannageDirectClient(), this.getTypeQuantite(), this.getDepot(), this.getModeRetour(), this.getModePriseEnCharge(), this.getModePriseEnChargeATC(), this.getMagasinRetourATC(), this.getClientInforme(), this.getEmails(), this.getPlateformeRetour(), event.target.value, null);


        onDetailChange(qualificationDetail, errors, null);
    }



    renderDepannageDirectClient() {
        const { classes, reclamation, utilisateurMagasin } = this.props;

        const detailDepannageDirectClientValue = this.getDepannageDirectClient();

        if (reclamation.type.code === TypeCodes.Client && utilisateurMagasin) {
            return (<div className={classes.row}>
                <DepannageDirectClient
                    onDetailChange={this.handleQualificationDetailsDepannageDirectClientChange}
                    value={detailDepannageDirectClientValue}
                />
            </div>);
        } else {
            return null;
        }
    }

    renderFormulaireRetourDepot() {
        const { classes, reclamation } = this.props;

        const depotGestionValue = this.getDepot();

        return (
            <div className={classes.row}>
                <Typography className={classes.row} variant="h6" gutterBottom>{`Magasin`}</Typography>
                <DepotGestionSelect
                    className={classes.formControl}
                    value={depotGestionValue}
                    valueIsCode
                    onChange={this.handleQualificationDetailsCodeDepot}
                    magasinsSeulement
                    isRequired
                    autoFocus
                    label="Sélectionner un magasin :"
                    modeTri={1}
                    variant="filled"
                    disabled={reclamation.magasin && reclamation.magasin.verrou}
                />
            </div>
        );
    }

    renderFormulaireMagasinRetourATC() {
        const { classes, reclamation } = this.props;

        const magasinRetourATCValue = this.getMagasinRetourATC();

        return (
            <div className={classes.row}>
                <Typography className={classes.row} variant="h6" gutterBottom>{`Magasin`}</Typography>
                <DepotGestionSelect
                    className={classes.formControl}
                    value={magasinRetourATCValue}
                    valueIsCode
                    onChange={this.handleQualificationDetailsMagasinRetourATC}
                    magasinsSeulement
                    isRequired
                    autoFocus
                    label="Sélectionner un magasin :"
                    modeTri={1}
                    variant="filled"
                    disabled={reclamation.magasin && reclamation.magasin.verrou}
                />
            </div>
        );
    }

    renderFormulaireDemandeEnlevement() {
        const { classes, transporteurInitial } = this.props;

        return (
            <Grid
                container
                direction="row"
                justify="space-between"
                alignContent="stretch"
                alignItems="stretch"
                spacing={1}
                className={classes.row}
            >
                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                        <Typography variant="h6" gutterBottom>{`Adresse d'enlèvement :`}</Typography>
                        <Stack spacing={1}>
                            <TextField required name="adresseEnlevementNom" label="Nom" value={this.state.adresseEnlevementNom} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField required name="adresseEnlevementAdresse1" label="Adresse" value={this.state.adresseEnlevementAdresse1} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField name="adresseEnlevementAdresse2" label="Complément" value={!this.state.adresseEnlevementAdresse2 ? '' : this.state.adresseEnlevementAdresse2} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField required name="adresseEnlevementCodePostal" label="Code Postal" value={this.state.adresseEnlevementCodePostal} onChange={e => this.handleChangeAdresseEnlevement(e)} />
                            <TextField required name="adresseEnlevementVille" label="Ville" value={this.state.adresseEnlevementVille} onChange={e => this.handleChangeAdresseEnlevement(e)} />
                        </Stack>
                    </Paper>
                </Grid>

                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>

                        <Typography variant="h6" gutterBottom>Adresse de destination :</Typography>

                        <TextField
                            id="adresseDestinationCode"
                            name="adresseDestinationCode"
                            label={"Destination"}
                            fullWidth
                            required
                            select

                            SelectProps={{
                                value: this.state.adresseDestinationCode,
                                onChange: e => this.handleChangeAdresseDestination(e)
                            }}
                        >

                            {AdresseDestinations.map(adresse => (

                                <MenuItem key={adresse.id} value={adresse.id}>{adresse.nom}</MenuItem>

                            ))}
                        </TextField>


                        {this.state.adresseDestinationCode === 3 && (
                            <Stack spacing={1} mt={1}>
                                <TextField required name="adresseDestinationNom" label="Nom" value={this.state.adresseDestinationNom} onChange={this.handleChangeAdresseDestination} fullWidth />
                                <TextField required name="adresseDestinationAdresse1" label="Adresse" value={this.state.adresseDestinationAdresse1} onChange={this.handleChangeAdresseDestination} fullWidth />
                                <TextField name="adresseDestinationAdresse2" label="Complément" value={this.state.adresseDestinationAdresse2} onChange={this.handleChangeAdresseDestination} fullWidth />
                                <TextField required name="adresseDestinationCodePostal" label="Code Postal" value={this.state.adresseDestinationCodePostal} onChange={this.handleChangeAdresseDestination} />
                                <TextField required name="adresseDestinationVille" label="Ville" value={this.state.adresseDestinationVille} onChange={this.handleChangeAdresseDestination} />
                            </Stack>
                        )}


                        {transporteurInitial && (
                            <div>

                                <Typography variant="h6" gutterBottom className={classes.marginTop}>Transporteur initial :</Typography>

                                <Typography gutterBottom>{transporteurInitial.nom} / {transporteurInitial.contrat}</Typography>

                            </div>
                        )}

                    </Paper>
                </Grid>

                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                        <Typography variant="h6" gutterBottom className={classes.marginTop}>Contact client nécessaire à l'enlèvement :</Typography>
                        <Stack spacing={1}>
                            <TextField required name="donneurOrdreNom" label="Nom" value={this.state.donneurOrdreNom} onChange={e => this.handleChangeDonneurOrdre(e)} fullWidth />
                            <TextField required name="donneurOrdreMail" label="Email" value={this.state.donneurOrdreMail} onChange={e => this.handleChangeDonneurOrdre(e)} fullWidth />
                            <TextField required name="donneurOrdreTelephone" label="Téléphone" value={this.state.donneurOrdreTelephone} onChange={e => this.handleChangeDonneurOrdre(e)} fullWidth />
                        </Stack>
                    </Paper>
                </Grid>

                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                        <Typography variant="h6" gutterBottom>Expédition :</Typography>
                        <Stack spacing={1}>


                            <MyDatePicker
                                name={ReclamationQualificationDetailCodes.DateEnlevement}
                                label="Date d'enlèvement souhaitée"
                                value={this.state[ReclamationQualificationDetailCodes.DateEnlevement]}
                                onChange={value => this.handleChangeDate({ target: { name: ReclamationQualificationDetailCodes.DateEnlevement, value: value } })}
                                disableFuture={false}
                                disablePast={true}
                                disableSunday={true}
                                fullWidth
                            />

                            <TextField
                                required
                                name={ReclamationQualificationDetailCodes.Poids}
                                label={qualificationDetailLabels[ReclamationQualificationDetailCodes.Poids]}
                                value={this.state[ReclamationQualificationDetailCodes.Poids]}
                                onChange={this.handleChangeInteger}
                                className={classes.marginTop}
                                fullWidth
                                inputProps={{ position: 8 }}
                            />

                            <Stack direction="row" spacing={1} justifyContent="space-evenly">
                                <TextField
                                    name={ReclamationQualificationDetailCodes.NombrePalettes}
                                    label="Nombre de palettes"
                                    type="number"
                                    value={this.state[ReclamationQualificationDetailCodes.NombrePalettes]}
                                    onChange={this.handleChangeInteger}
                                    inputProps={{ position: 9 }}
                                />
                                <TextField
                                    name={ReclamationQualificationDetailCodes.NombreColis}
                                    label="Nombre de colis"
                                    type="number"
                                    value={this.state[ReclamationQualificationDetailCodes.NombreColis]}
                                    onChange={this.handleChangeInteger}
                                    inputProps={{ position: 10 }}
                                />
                                <TextField
                                    name={ReclamationQualificationDetailCodes.NombreLongueurs}
                                    label="Nombre de longueur > 1,20 m"
                                    type="number"
                                    value={this.state[ReclamationQualificationDetailCodes.NombreLongueurs]}
                                    onChange={this.handleChangeInteger}
                                    inputProps={{ position: 11 }}
                                />
                            </Stack>

                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    renderFormulaireRetourConditions() {
        const { classes } = this.props;

        const clientInformeValue = this.getClientInforme();

        return (
            <React.Fragment>

                <Typography className={classes.row} variant="h6" gutterBottom>{`Merci de préciser au client les conditions de retour de la marchandise.`}</Typography>

                <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Je confirme que ...`}</Typography>

                <ul className={classes.row}>
                    <li>
                        Les produits sont en état de revente
                    </li>
                    <li>
                        Les produits sont dans leur emballage d'origine
                    </li>
                    <li>
                        Ni les emballages ni les produits ne sont détériorés, marqués, scotchés, découpés, ...
                    </li>
                    <li>
                        Les produits ne sont pas périssables et ne font pas l'objet d'une date limite de consommation ...
                    </li>
                    <li>
                        Les produits retournés (articles et quantités) sans déclaration et acceptation préalable ne feront pas l'objet d'avoir
                    </li>
                    <li>
                        Le retour physique doit avoir lieu dans les 15 jours suite à la déclaration de ce retour
                    </li>

                </ul>

                <FormControlLabel className={classes.row}
                    control={
                        <Checkbox
                            checked={clientInformeValue}
                            onChange={e => this.handleQualificationDetailsClientInformeChange({ checked: e.target.checked, position: 10, libelle: clientInformeLabel })}
                            value="true"
                        />
                    }
                    label={clientInformeLabel}
                />
            </React.Fragment>
        );
    }



    renderPriseConnaissancePhoto() {
        const { classes, reclamation, colisLogistiques } = this.props;

        const photoConsulteeValue = this.getPhotoConsultee();

        let reclaCodes = reclamation.lignes.map(x => x.codeArticle);



        let colisLogistiquesPhoto = colisLogistiques.map(x => {

            let codesArticles = x.lignes.map(x => x.codeArticle);
            let c = {
                photoId: x.photoId,
                numeroSupport: x.numeroSupport,
                concerneRecla: reclaCodes.some(r => codesArticles.indexOf(r) >= 0)
            };
            return c;
        });

        //console.log("colisLogistiquesPhoto", colisLogistiquesPhoto);

        let afficherQuestion = colisLogistiquesPhoto.some(x => x.concerneRecla && x.photoId);

        return (

            <>

                {afficherQuestion && (
                    <>

                        <div className={classes.row}>

                            <FormControl required className={classes.formControl}>
                                <FormLabel component="legend" sx={{ display: 'flex' }}>
                                    {photoConsulteeLabel}

                                    {colisLogistiquesPhoto.map((colis, index) => {
                                        return (
                                            <ColisLogistiquePhoto
                                                key={index}
                                                colis={colis}
                                                disabledMessage={!colis.photoId ? "pas de photo de ce colis" : !colis.concerneRecla ? "colis non concerné" : ""}
                                                mode={"button"}
                                                size={"large"}
                                            />
                                        );
                                    })}

                                </FormLabel>
                                <RadioGroup
                                    id={ReclamationQualificationDetailCodes.PhotoConsultee}
                                    name={ReclamationQualificationDetailCodes.PhotoConsultee}
                                    className={classes.group}
                                    value={photoConsulteeValue}
                                    onChange={this.handleQualificationDetailsPhotoConsulteeChange}>
                                    {photoConsulteeLabels.map((reponse, index) => (
                                        <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                    ))}
                                </RadioGroup>
                            </FormControl>

                        </div>

                    </>
                )}
            </>


        );

    }


    render() {
        const { classes, reclamation, namespace, agePiece, exclusionControleAgePiece } = this.props;
        const { etape, expandedPanel, typeErreur, fautif } = this.state;

        const typeQuantiteValue = this.getTypeQuantite();
        const modeRetourValue = this.getModeRetour();
        const modePriseEnChargeValue = this.getModePriseEnCharge();
        const modePriseEnChargeATCValue = this.getModePriseEnChargeATC();

        const plateformeRetourCode = this.getPlateformeRetour();
        const adresse = this.getDetail(ReclamationQualificationDetailCodes.AdresseRetour, 'valeurString');

        let quantiteProduitsRepris = reclamation.lignes.reduce((previous, current) => previous + Number(current.quantiteAnomalie), 0);

        return (
            <React.Fragment>
                <div className={classes.row}>
                    <TextField
                        multiline
                        fullWidth
                        name="commentaire"
                        label="Commentaire"
                        placeholder="Décrire le problème rencontré"
                        maxRows="3"
                        onChange={this.handleChange}
                        value={reclamation.commentaire}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </div>

                <div className={classes.row}>
                    <DropZone id="fichiersIds" name="fichiersIds" onChange={this.handleChange} />
                </div>

                <div className={classes.row}>
                    <Accordion expanded={expandedPanel === 'panelPiece'} onChange={this.handleChangePanel('panelPiece')}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            className={classes.panelSummary}
                        >
                            <Typography >Rechercher une pièce* (obligatoire pour accéder aux lignes)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <PieceGestionSearch
                                onSelectPiece={this.handleSelectPiece}
                                client={reclamation.client}
                                withLignes
                                namespace={namespace}
                                warningReclamation
                                defautProduitInfos={false}
                                logistiqueInfos
                                defautNumeroPiece={reclamation.numeroPiece}
                            />
                        </AccordionDetails>
                    </Accordion>


                    {reclamation.numeroPiece && etape === Etape.Qualifications && (
                        <>

                            <Accordion expanded={expandedPanel === 'panelQuantite'} onChange={this.handleChangePanel('panelQuantite')}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    className={classes.panelSummary}
                                >
                                    <Typography>
                                        {typeQuantiteLabel}
                                        {expandedPanel !== 'panelQuantite' && typeQuantiteValue !== '' && (
                                            <span className={classes.italic}>{getTypeQuantiteLabel(typeQuantiteValue)}</span>
                                        )}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <RadioGroup id={ReclamationQualificationDetailCodes.TypeQuantite} name={ReclamationQualificationDetailCodes.TypeQuantite} className={classes.group} value={typeQuantiteValue} onChange={this.handleQualificationDetailsTypeQuantiteChange}>
                                        {typeQuantiteLabels.map((reponse, index) => {
                                            // Filtre des questions si la date de livraison de la commande est inférieur à 30 jours
                                            // ou si le type de la réclamation est différent de `CESSION`
                                            if (reponse.autorise(agePiece, reclamation.type, exclusionControleAgePiece)) {
                                                return <FormControlLabel key={index} value={reponse.id.toString()} control={<Radio />} label={reponse.label} />
                                            }
                                        })}
                                    </RadioGroup>
                                </AccordionDetails>
                            </Accordion>

                            {(estTypeQuantiteSelectionLigne(typeQuantiteValue) || typeQuantiteValue === '2') &&
                                <Accordion sx={{ mt: 2 }} expanded={expandedPanel === 'panelLignes'} onChange={this.handleChangePanel('panelLignes')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.panelSummary}
                                    >
                                        <Typography >Sélectionner une ligne* (au moins une ligne en anomalie pour pouvoir créer le dossier après-vente)</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <div className={classes.row}>
                                            {/* DEV-4347 : la colonne quantité retournable est affichée dans tous les cas ;
                                                le plafonnement (capperSaisie) est piloté par la config typeQuantiteLabels —
                                                capperSaisie=true pour « il m'en manque » (id 3) et les demandes de retour
                                                (id 1/2), false pour « j'en ai trop / non facturé » (id 4), où la saisie
                                                reste libre (déclaration d'écart, pas de retour financier). */}
                                            <LigneGestionQualification
                                                reclamation={reclamation}
                                                onAddLigne={this.handleAddLigne}
                                                onRemoveLigne={this.handleRemoveLigne}
                                                typeAnomalie={typeQuantiteValue === '2' ? AnomalieType.DemandeRetour : AnomalieType.QuantiteArticleIncorrecte}
                                                disableCompose={typeQuantiteValue === '2' ? false : true}
                                                namespace={namespace}
                                                quantiteLibre={estTypeQuantiteSelectionLigne(typeQuantiteValue) && !capperSaisieTypeQuantite(typeQuantiteValue)}
                                                afficherQuantiteRetournable
                                                capperSaisie={capperSaisieTypeQuantite(typeQuantiteValue)}
                                            />
                                        </div>
                                    </AccordionDetails>
                                </Accordion>}

                            {typeQuantiteValue === '2' && (
                                <Accordion expanded={true}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.panelSummary}
                                    >
                                        <Typography>{typeErreurLabel}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <div className={classes.row}>
                                            <RadioGroup id="typeErreur" name="typeErreur" className={classes.group} value={typeErreur} onChange={this.handleChangeTypeErreur}>
                                                {typeErreurLabels.map((reponse, index) => (
                                                    <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                                ))}
                                            </RadioGroup>
                                            <Typography >par</Typography>
                                            <RadioGroup id="fautif" name="fautif" className={classes.group} value={fautif} onChange={this.handleChangeTypeErreur}>
                                                {fautifLabels.map((reponse, index) => (
                                                    <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                                ))}
                                            </RadioGroup>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {estTypeQuantiteSelectionLigne(typeQuantiteValue) && (
                                <div className={classes.row}>
                                    {this.renderPriseConnaissancePhoto()}
                                </div>
                            )}

                            {(typeQuantiteValue === '2') &&
                                <Accordion expanded={true}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.panelSummary}
                                    >
                                        <Typography >Choix du mode de retour</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <RadioGroup id={ReclamationQualificationDetailCodes.ModeRetour} name={ReclamationQualificationDetailCodes.ModeRetour} className={classes.group} value={modeRetourValue} onChange={this.handleQualificationDetailsModeRetourChange}>
                                            {modeRetour.map((reponse, index) => (
                                                <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                            ))}
                                        </RadioGroup>
                                    </AccordionDetails>
                                </Accordion>}

                            {(typeQuantiteValue === '2' && modeRetourValue === '2') &&
                                <Accordion expanded={expandedPanel === 'panelModePriseEnCharge'} onChange={this.handleChangePanel('panelModePriseEnCharge')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.panelSummary}
                                    >
                                        <Typography >Mode de prise en charge</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <RadioGroup id={ReclamationQualificationDetailCodes.ModePriseEnCharge} name={ReclamationQualificationDetailCodes.ModePriseEnCharge} className={classes.group} value={modePriseEnChargeValue} onChange={this.handleQualificationDetailsModePriseEnChargeChange}>
                                            {modePriseEnCharge.map((reponse, index) => (
                                                <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                            ))}
                                        </RadioGroup>
                                    </AccordionDetails>
                                </Accordion>}

                            {(typeQuantiteValue === '2' && modeRetourValue === '3') &&
                                <Accordion expanded={expandedPanel === 'panelModePriseEnChargeATC'} onChange={this.handleChangePanel('panelModePriseEnChargeATC')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.panelSummary}
                                    >
                                        <Typography >Mode de prise en charge</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <RadioGroup id={ReclamationQualificationDetailCodes.ModePriseEnChargeATC} name={ReclamationQualificationDetailCodes.ModePriseEnChargeATC} className={classes.group} value={modePriseEnChargeATCValue} onChange={this.handleQualificationDetailsModePriseEnChargeATCChange}>
                                            {modePriseEnChargeATC.map((reponse, index) => (
                                                <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                            ))}
                                        </RadioGroup>
                                    </AccordionDetails>
                                </Accordion>}

                            {modeRetourValue === '0' &&
                                (this.renderFormulaireDemandeEnlevement())
                            }

                            {modeRetourValue === '1' &&
                                (this.renderFormulaireRetourDepot())
                            }

                            {modeRetourValue === '2' && modePriseEnChargeValue === '0' &&
                                (
                                    <div className={classes.row} style={{ display: 'flex' }}>
                                    <DepotGestionSelect
                                        style={{ width: '400px' }}
                                        className={classes.formControl}
                                        value={plateformeRetourCode}
                                        valueIsCode
                                        onChange={this.handleQualificationDetailsPlateformeRetour}
                                        plateformesSeulement
                                        isRequired
                                        autoFocus
                                        label="Sélectionner une plateforme :"
                                        modeTri={1}
                                        variant="filled"
                                        fullWidth={false}
                                    />

                                        <Card className={classes.card} style={{ marginLeft: '16px' }}>
                                            <CardContent>
                                                <Typography gutterBottom variant="h6">
                                                    Adresse de la plateforme selectionnée
                                                </Typography>
                                                {adresse.split('\n').map((ligne, index) => (
                                                    <Typography gutterBottom variant="body1" key={index}>
                                                        {ligne}
                                                    </Typography>
                                                ))}
                                            </CardContent>
                                        </Card>


                                    </div>
                                )
                            }

                            {modeRetourValue === '2' && modePriseEnChargeValue === '1' &&
                                (this.renderFormulaireDemandeEnlevement())
                            }

                            {modeRetourValue === '3' && modePriseEnChargeATCValue === '0' &&
                                (this.renderFormulaireMagasinRetourATC())
                            }



                            {(reclamation.lignes.length > 0 && typeQuantiteValue === '2') && (
                                <Accordion expanded={true}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={classes.panelSummary}
                                    >
                                        <Typography >Résumé</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetailsRow}>


                                        <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Montant total des lignes : ${reclamation.retour.montant.toFixed(2)} €`}</Typography>

                                        {
                                            (quantiteProduitsRepris !== 0) && (
                                                <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Quantité de produits repris : ${quantiteProduitsRepris}`}</Typography>
                                            )
                                        }

                                        <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Pas de décote`}</Typography>
                                        <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Frais de retour : 0.00 €`}</Typography>
                                        <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Montant estimé de l'avoir : ${reclamation.retour.montant.toFixed(2)} €`}</Typography>

                                        {this.renderFormulaireRetourConditions()}

                                    </AccordionDetails>
                                </Accordion>
                            )}

                        </>
                    )}




                </div>

                {modeRetourValue && modeRetourValue !== '' &&

                    <MultiSelectMailContactClient
                        onChange={(emails) => this.handleQualificationDetailsEmails(emails)}
                        clientId={reclamation.client.id}
                        label="Mail(s) pour notification de la reprise*"
                        suiviEmail={reclamation.suiviEmail}
                    />
                }

                {this.renderDepannageDirectClient()}


            </React.Fragment>
        );
    }
}

QuantiteArticleIncorrecteStep.propTypes = {
    reclamation: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onDetailChange: PropTypes.func.isRequired,
    onSelectPiece: PropTypes.func.isRequired,
    onAddLigne: PropTypes.func.isRequired,
    onRemoveLigne: PropTypes.func.isRequired,
    onAddRetour: PropTypes.func.isRequired,
    onRemoveRetour: PropTypes.func.isRequired,
    validationErrors: PropTypes.array.isRequired,
    onNextStepValid: PropTypes.func.isRequired,
    namespace: PropTypes.string.isRequired,
    colisLogistiques: PropTypes.array.isRequired,
};

export default withStyles(styles)(QuantiteArticleIncorrecteStep);
